/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { ClassificationLabel } from "./classification-label";
import { EntityLabel } from "./entity-label";
import { GroupingLabel } from "./grouping-label";

type TabType = "DocSplitter" | "DAI_COMPLEX_CLASSIFICATION" | "Entity";

/**
 * Document input
 */
export class DocumentInput {
  data: any;
  navigation: LayoutConfig;

  constructor(
    labels: EntityLabel[] | GroupingLabel[] | ClassificationLabel[],
    headerTitle: string,
    disablePrev: boolean = true,
    disableNext: boolean = true,
    type: TabType = "Entity",
    schemaFirst: boolean = true
  ) {
    let documentAnnotationData: any = {};

    documentAnnotationData.pages = [{
      "imageUrl": {
        "privateDoNotAccessOrElseWrappedUrl": ""
      },
      "tokens": [],
      "kvPairs": [],
      "entities": [],
      "tables": [],
      "textBlocks": [],
      "angle": 0
    }];

    // convert Document to DocumentAnnotationData which is different for splitting and entity extraction
    if (type === "DocSplitter" ) {
      // set groupingLabels
      documentAnnotationData.entityLabels = [];
      documentAnnotationData.groupingLabels = labels;
      documentAnnotationData.editConfig = {
        // enable refreshed CDS UI
        cdsRefresh: true,
      };
    } else if (type === "DAI_COMPLEX_CLASSIFICATION") {
      documentAnnotationData.entityLabels = [];
      documentAnnotationData.classificationLabels = labels;
    } else {
      documentAnnotationData.entityLabels = labels;
       documentAnnotationData.editConfig = {
        // enable support for nested entities
        enableNestedEntities: true,
        // enable schema first labeling, e.g. show all values from the entityLabels even if they are not set in the document
        schemaFirst: schemaFirst,
      };
    }    
    // Whether rotate functionality should be activated or not. Defaults to false.
    documentAnnotationData.allowRotate = true;
    // Whether to show the escalate button.
    documentAnnotationData.showEscalateButton = true;
    // Display name of the document.
    documentAnnotationData.documentName = headerTitle;

    this.data = documentAnnotationData;

    // setup navigation layout
    this.navigation = new LayoutConfig(
      headerTitle,
      disablePrev,
      disableNext,
      type
    );
  }
}

// the LayoutConfig class sets up the look of the UI - most customizations can be done here
export class LayoutConfig {
  appMode = "HumanLabeling";
  // Controls whether to show/hide action button.
  isEditable = true;
  // Whether there was a local change, according to if there was/was not, the action button will be enabled/disabled. Required when isEditable is true.
  dataChanged = false;
  // Title for side panel header.
  headerTitle: string;
  // Controls whether to always enable primary button.
  enablePrimaryButton = true;
  // Button text for save button. Default value is 'Save'.
  primaryButtonText = "Bestätigen";
  // Optional text for secondary button. If not given, the secondary button will be invisible.
  secondaryButtonText = "Ablehnen";
  // Disable previous button - will be overwritten by the constructor
  disablePrev: boolean;
  // Disable next button - will be overwritten by the constructor
  disableNext: boolean;
  // Disable the confirm dialog for unsaved warning
  disableConfirmDialog = false;
  // Controls whether to show/hide header - will be overwritten by the constructor
  hideHeader = false;
  // Controls whether to show/hide back button.
  hideBackButton = false;
  // set the current tab type - will be overwritten by the constructor
  tabGroupConfig = {
    currentTabType: "Entity",
  };

  constructor(
    headerTitle: string,
    disablePrev: boolean = true,
    disableNext: boolean = true,
    type: TabType
  ) {
    this.headerTitle = headerTitle;
    this.disablePrev = disablePrev;
    this.disableNext = disableNext;
    this.tabGroupConfig.currentTabType = type;
  }
}
